<template>
  <div class="box-border">
    <div class="review-box justify-content-between">
      <div class="review-name">                  
        <div>Reviewed by:</div>
        <div
          class="overflow-text"
          :title="reviewerName"
        >
          {{ reviewerName }}
        </div>
      </div>
      <div class="review-time">
        <div>Last Saved:</div>
        <div v-if="isReviewed">
          {{ dateTimeFormat(slideInfo[$const.SLIDE_KEYS.REVIEW_TIME]) }}
        </div>
      </div>
      <div
        v-if="!isReadOnly"
        class="save-button-container"
      >
        <BButton
          class="save-button action-button"
          variant="primary"
          :disabled="isBusy"
          @click="handleSave"
        >
          Save
        </BButton>
      </div>
      <div v-if="!isReadOnly">
        <BButton
          class="confirm-button action-button"
          variant="success"
          :disabled="isBusy"
          @click="handleConfirm"
        >
          {{ reviewButtonText }}
        </BButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, isRef, unref, onMounted } from 'vue';
import { BButton } from 'bootstrap-vue-next';
import { useConfigStore } from '@/store';
import { useTime } from '@/composables/useTime';
import useCells from '@/composables/useCells';
import type { CellGroupsPostPayload } from '@/types/api';
import { useConstants } from '@/composables/useAppGlobals';
import { useRouter } from 'vue-router';
import { reportReviewInjectionKey, type ProvidedReportReviewContext, type ReportReviewContext } from './reportReviewContext';

const config = useConfigStore();
const $const = useConstants();
const router = useRouter();

type StateKey = keyof typeof $const.TASK_STATES_TRANSFORM;

const providedContext = inject(reportReviewInjectionKey, null) as ProvidedReportReviewContext | null;
const fallbackContext = useCells();
const reviewContext = computed<ReportReviewContext>(() => {
  if (providedContext) {
    return isRef(providedContext) ? providedContext.value : providedContext;
  }
  return fallbackContext;
});

const slideInfo = computed<Record<string, unknown>>(() => unref(reviewContext.value.slideInfo) ?? {});
const isReviewed = computed(() => Boolean(slideInfo.value[$const.SLIDE_KEYS.REVIEWER]));
const isReadOnly = computed(() => config.isReadOnly);
const isBusy = computed(() => reviewContext.value.isBusy.value);

const currentState = computed(() => slideInfo.value[$const.SLIDE_KEYS.STATE]);
const nextState = computed(() => {
  if (currentState.value === $const.TASK_STATES.DONE) {
    return $const.TASK_STATES.DONE;
  }
  return $const.TASK_STATES_TRANSFORM[currentState.value as StateKey];
});
const reviewButtonText = computed(() => (currentState.value === $const.TASK_STATES.DONE ? 'Override' : 'Complete'));
const reviewerName = computed(() => (isReviewed.value ? String(slideInfo.value[$const.SLIDE_KEYS.REVIEWER] ?? '') : ''));

const { dateTimeFormat: formatDateTime } = useTime();

function dateTimeFormat(date: unknown) {
  return formatDateTime(date as Parameters<typeof formatDateTime>[0]);
}

async function handleSave() {
  await reviewContext.value.save();
}

async function handleConfirm() {
  const ctx = reviewContext.value;
  ctx.isBusy.value = true;
  try {
    const payload: CellGroupsPostPayload = {
      ...(ctx.cellBackendRequestObj.value as Record<string, unknown>),
      state: nextState.value,
    } as CellGroupsPostPayload;
    await ctx.postCells(payload);
    closeConfirmModal();
  } finally {
    ctx.isBusy.value = false;
  }
}

function closeConfirmModal() {
  router.push($const.ROUTES.DASHBOARD);
}

onMounted(() => {
  config.setIsBusy(false);
});
</script>

<style lang="scss" scoped>

.review-box {
  display: flex;
  flex-direction: row;
  position: relative;
}
.review-name {
  width: 150px;
  margin-top: -1px;
}

.overflow-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.review-time {
  width: 200px;
  margin-top: -1px;
}

.action-button {
  margin-top: 3px;
  margin-right: 10px;
  padding: 2px 5px;
  line-height: 26px;
  border-radius: 5px;
  letter-spacing: 0.7px;
  font-size: 18px;
}
.confirm-button {
  width: 120px;
}
.save-button {
  width: 108px;
}
</style>
